/**
 * The world's answers to the game: a tile pops and sparks when it is built, grows, gets a machine,
 * is collected from or gathered, its extractor starts or finishes an upgrade, a charge breaks it,
 * or an infuser is loaded or lays a block on it. My own actions answer at once from the prediction; everyone else's when the server confirms
 * them.
 */
import {
  blockOf,
  MACHINES,
  machineAt,
  type Kind,
  type MachineType,
} from '@bramvasterd/terrella-rules';

import type { Loop } from '@core/loop';
import {
  RESOURCE_SPARK,
  SPARK_BLAST,
  SPARK_BUILD,
  SPARK_FALL,
  SPARK_GROW,
  SPARK_SINK,
} from '@core/palette';
import type { Game } from '@game/game';
import type { GameEvents } from '@game/types';

import type { Me } from '../actors/me';
import type { Display } from '../planet/display';
import type { Effects } from './effects';

export interface ReactionDeps {
  game: Game;
  display: Display;
  effects: Effects;
  me: Me;
  loop: Loop;
}

/** The sparks of a machine type: what an extractor draws; a charge its hot pink. */
function sparkOf(type: MachineType | undefined): number {
  const spec = type ? MACHINES[type] : null;
  if (spec?.role === 'charge') return SPARK_BLAST;
  return spec?.role === 'extractor' ? (RESOURCE_SPARK[spec.resource] ?? SPARK_BUILD) : SPARK_BUILD;
}

/** The sparks of the block a floor of `kind` gives. */
function blockSpark(kind: Kind | undefined): number {
  const block = kind === undefined ? null : blockOf(kind);
  return (block && RESOURCE_SPARK[block]) ?? SPARK_BUILD;
}

/** Listen to the game; returns the function that stops listening. */
export function reactToGame({ game, display, effects, me, loop }: ReactionDeps): () => void {
  const { pop, burst } = effects;

  const confirmed = ({ events, mine }: GameEvents['confirmed']): void => {
    const now = performance.now();
    if (display.mode !== 'game') return;
    // my own changes popped already, when I made them
    const predicted = mine && game.store.get().predicting;
    for (const e of events) {
      switch (e.kind) {
        case 'grew':
          pop(e.tile, now);
          burst(e.tile, SPARK_GROW, 16);
          break;
        case 'fell':
          pop(e.tile, now);
          burst(e.tile, SPARK_FALL, 18);
          break;
        case 'sank':
          // the whole pond settles lower: every tile of it dips, predicted or not
          for (const t of e.tiles) {
            pop(t, now);
            burst(t, SPARK_SINK, 8);
          }
          break;
        case 'placed':
          if (predicted) break;
          pop(e.tile, now);
          burst(e.tile, sparkOf(e.machine), 18);
          break;
        case 'collected':
          if (!predicted) burst(e.tile, RESOURCE_SPARK[e.item] ?? SPARK_BUILD, 16);
          break;
        case 'gathered':
          // the block came out by hand: the tile sinks a level and sparks in the block's colour
          if (predicted) break;
          pop(e.tile, now);
          burst(e.tile, RESOURCE_SPARK[e.block] ?? SPARK_BUILD, 16);
          break;
        case 'loaded':
          if (!predicted) burst(e.tile, RESOURCE_SPARK[e.item] ?? SPARK_BUILD, 12);
          break;
        case 'laid':
          // a block landed, for everyone at once: the tile rises and sparks in its colour
          pop(e.tile, now);
          burst(e.tile, RESOURCE_SPARK[e.block] ?? SPARK_BUILD, 18);
          break;
        case 'shattered':
          // the charge went off, for everyone at once: every tile it broke jumps and flares
          pop(e.tile, now);
          for (const t of e.tiles) {
            if (t !== e.tile) pop(t, now);
            burst(t, SPARK_BLAST, 24);
          }
          break;
        case 'removed':
          if (predicted) break;
          pop(e.tile, now);
          burst(e.tile, SPARK_FALL, 14);
          break;
        case 'upgrading':
          if (predicted) break;
          pop(e.tile, now);
          burst(e.tile, SPARK_BUILD, 18);
          break;
        case 'upgraded': {
          // its time is up, for everyone at once: it stands there a level higher
          const view = game.store.get().view;
          pop(e.tile, now);
          burst(e.tile, sparkOf(view ? machineAt(view, e.tile)?.type : undefined), 28);
          break;
        }
        default:
          break;
      }
    }
  };

  /** My action, the moment I make it (while predicting): it pops and sparks before the server answers. */
  const intent = (queued: GameEvents['intent']): void => {
    const s = game.store.get();
    if (queued.kind !== 'action' || display.mode !== 'game' || !s.predicting || !s.view) return;
    const action = queued.action;
    const now = performance.now();
    const { tile } = action;
    pop(tile, now);
    if (action.kind === 'place') burst(tile, sparkOf(action.machine), 18);
    else if (action.kind === 'collect') burst(tile, sparkOf(machineAt(s.view, tile)?.type), 16);
    // the view is predicted past it already: the block came from the floor the server knows
    else if (action.kind === 'gather') burst(tile, blockSpark(s.confirmed?.kind[tile] as Kind), 16);
    else if (action.kind === 'load') burst(tile, RESOURCE_SPARK[action.item] ?? SPARK_BUILD, 12);
    else if (action.kind === 'remove') burst(tile, SPARK_FALL, 14);
    else burst(tile, SPARK_BUILD, 18); // an upgrade starts
  };

  const stops = [
    game.events.on('confirmed', confirmed),
    game.events.on('intent', intent),
    game.events.on('hop', () => {
      me.hop(performance.now());
      loop.wake(500);
    }),
  ];
  return () => {
    for (const stop of stops) stop();
  };
}
